'use client';

/**
 * Forked from packages/framework/ErrorHandling/GlobalError/GlobalErrorContext.tsx
 * so CFR alerts go through @creditflowresearch/uilib/Alert instead of @dealroadshow/uikit-alert.
 * App Router migration: apps/allocate/app/layout.tsx imports the shared framework version
 * directly (still uikit-alert) — do NOT swap this fork back to that import; keep this file
 * (already @framework/next/navigation-based, works in both routers) and repoint it at app/.
 */
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useSearchParams, usePathname, useRouter } from '@framework/next/navigation';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import { useMessageManager } from '@framework/message';

interface IError {
  code: string,
  data: object,
}

interface IProps {
  children: React.ReactNode,
}

const useGlobalError = () => {
  const errorPathRef = useRef<string | null>(null);
  const [isGlobalErrorVisible, setIsGlobalErrorVisible] = useState<boolean>(false);
  const [error, setError] = useState<IError | {}>(null);
  const [message, setMessage] = useState<string | null>(null);

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const replaceUrl = ({ pathname, search }: { pathname: string, search?: string }): void => {
    const url = search ? `${ pathname }?${ search }` : pathname;
    router.replace(url);
  };

  const { getMessageByErrorCode } = useMessageManager();

  useEffect(() => {
    checkGlobalErrors(pathname);
  }, [pathname]);

  const checkGlobalErrors = (currentPathname: string) => {
    if (errorPathRef.current && currentPathname !== errorPathRef.current) {
      errorPathRef.current = null;
      hideGlobalError();
    }

    // @ts-ignore
    const errorCode = +searchParams.get('error_code');

    if (!errorCode) {
      return;
    }
    const rawErrorData = searchParams.get('error_data');
    const errorData = rawErrorData ? JSON.parse(decodeURIComponent(rawErrorData)) : {};
    const showToast = searchParams.get('show_toast');
    const errorMessage = getMessageByErrorCode(errorCode, errorData);

    if (showToast) {
      AlertManager.error(errorMessage);
      return;
    }
    errorPathRef.current = currentPathname;
    showGlobalError(errorMessage, { code: errorCode, data: errorData });

    const remainingParams = new URLSearchParams(searchParams.toString());
    remainingParams.delete('error_code');
    remainingParams.delete('error_data');
    remainingParams.delete('show_toast');

    replaceUrl({ pathname, search: remainingParams?.toString() || undefined });
  };

  const showGlobalError = (message: string, error: IError | {} = {}) => {
    setError(error);
    setMessage(message);
    setIsGlobalErrorVisible(true);
  };

  const hideGlobalError = () => {
    setIsGlobalErrorVisible(false);
  };

  return {
    isGlobalErrorVisible,
    error,
    message,
    hideGlobalError,
  };
};

type GlobalErrorContextType = ReturnType<typeof useGlobalError>;

// @ts-ignore
export const GlobalErrorContext = createContext<GlobalErrorContextType>(null);

export const useGlobalErrorContext = (): GlobalErrorContextType => {
  const context = useContext<GlobalErrorContextType>(GlobalErrorContext);
  if (!context) {
    throw new Error('useGlobalErrorContext must be used within a GlobalErrorContextProvider');
  }
  return context;
};

export default ({ children }: IProps) => (
  <GlobalErrorContext.Provider value={ useGlobalError() }>
    { children }
  </GlobalErrorContext.Provider>
);
